import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { EpisodeSummary, HeroPublic, Me } from "@scrile/contracts";
import { useApi } from "./api/context";
import { describeError, isApiError } from "./api/errors";
import { MiniPlayer } from "./components/MiniPlayer";
import { Spinner } from "./components/Spinner";
import { TopBar } from "./components/TopBar";
import { t } from "./i18n";
import { track } from "./lib/analytics";
import { hasAccess } from "./lib/episodes";
import { chatFromListen, href, navigate, screenOf, useRoute } from "./lib/router";
import { PlayerProvider, usePlayer } from "./player/PlayerContext";
import { HeroDataProvider, type HeroData } from "./state/heroData";
import { applyTheme } from "./theme";
import { Account } from "./views/Account";
import { Auth } from "./views/Auth";
import { HeroPage } from "./views/HeroPage";
import { Messenger } from "./views/Messenger";
import { NotFound } from "./views/NotFound";
import { Paywall } from "./views/Paywall";
import { Player } from "./views/Player";

/**
 * Сайт героя: один на все поддомены. Сначала — кто герой (GET /hero по Host) и кто
 * вошёл (GET /me), потом серии: их доступность зависит от человека.
 */
export function App() {
  const api = useApi();
  const route = useRoute();
  const [hero, setHero] = useState<HeroPublic | null>(null);
  const [heroError, setHeroError] = useState<unknown>(null);
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [episodes, setEpisodes] = useState<EpisodeSummary[]>([]);
  const [episodesReady, setEpisodesReady] = useState(false);
  const [episodesError, setEpisodesError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const lastScreen = useRef<string | null>(null);

  // page_view — на каждый новый экран, а не на каждый адрес: /listen/1 → /listen/2 тот же
  // «Плеер». Ref, а не состояние: двойной эффект StrictMode не даёт второго события
  const screen = screenOf(route);
  useEffect(() => {
    if (screen === null || screen === lastScreen.current) return;
    lastScreen.current = screen;
    track("page_view", { screen });
  }, [screen]);

  useEffect(() => {
    let cancelled = false;
    setHeroError(null);
    api
      .hero()
      .then((loaded) => {
        if (cancelled) return;
        applyTheme(loaded.theme, loaded.name);
        document.title = loaded.name;
        setHero(loaded);
      })
      .catch((error: unknown) => !cancelled && setHeroError(error));
    api
      .me()
      .then((loaded) => !cancelled && setMe(loaded))
      // Не узнали, кто это, — считаем гостем: витрина важнее, войти можно и потом
      .catch(() => !cancelled && setMe(null));
    return () => {
      cancelled = true;
    };
  }, [api, attempt]);

  const reloadEpisodes = useCallback(async () => {
    try {
      const list = await api.episodes();
      setEpisodes(list);
      setEpisodesError(null);
      setEpisodesReady(true);
      return list;
    } catch (error) {
      setEpisodesError(describeError(error));
      throw error;
    }
  }, [api]);

  const reloadMe = useCallback(async () => {
    const fresh = await api.me();
    setMe(fresh);
    return fresh;
  }, [api]);

  // Доступность серий зависит от подписки: новый человек или новый статус — новый список
  const accessKey =
    me === undefined ? null : me === null ? "guest" : `${me.id}:${me.subscription?.status ?? "none"}:${String(me.unlockedAll)}`;
  // Серии нужны и переписке: названия для разделителей «Episode N» и номер для пейвола
  useEffect(() => {
    if (!hero || accessKey === null) return;
    let cancelled = false;
    api
      .episodes()
      .then((list) => {
        if (cancelled) return;
        setEpisodes(list);
        setEpisodesError(null);
        setEpisodesReady(true);
      })
      .catch((error: unknown) => !cancelled && setEpisodesError(describeError(error)));
    return () => {
      cancelled = true;
    };
  }, [api, hero, accessKey]);

  // Подписчик героя-переписки открыл сайт — сразу в переписку: для него это и есть
  // главный экран. Только при заходе: ссылка «назад» из переписки ведёт на страницу героя
  const landed = useRef(false);
  useEffect(() => {
    if (landed.current || !hero || me === undefined) return;
    landed.current = true;
    if (route.name === "hero" && hero.format === "chat" && me && hasAccess(me)) navigate(href.chat(), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hero, me]);

  const data = useMemo<HeroData | null>(
    () =>
      hero && me !== undefined
        ? { hero, me, episodes, episodesReady, episodesError, setMe: (next) => setMe(next), reloadMe, reloadEpisodes }
        : null,
    [hero, me, episodes, episodesReady, episodesError, reloadMe, reloadEpisodes],
  );

  if (heroError) return <Fatal error={heroError} onRetry={() => setAttempt((n) => n + 1)} />;
  if (!data || !hero) return <Splash />;

  let view: ReactNode;
  switch (route.name) {
    case "hero":
      view = <HeroPage />;
      break;
    case "listen":
      // Возврат из оплаты (RETURN_PATHS ведут в «Плеер») у переписки — в «Мессенджер»
      view = hero.format === "audio" ? <Player route={route} /> : <Redirect to={chatFromListen(route)} />;
      break;
    case "plans":
      view = <Paywall route={route} />;
      break;
    case "join":
    case "signin":
      view = data.me ? (
        <Redirect to={route.next ?? href.listen()} />
      ) : (
        <Auth key={route.name} mode={route.name === "join" ? "register" : "login"} next={route.next} />
      );
      break;
    case "account":
      view = data.me ? <Account route={route} me={data.me} /> : <Redirect to={href.signin(href.account())} />;
      break;
    case "chat":
      view =
        hero.format !== "chat" ? (
          <Redirect to={href.listen()} />
        ) : data.me ? (
          <Messenger key={data.me.id} route={route} me={data.me} />
        ) : (
          // Лента личная: без аккаунта её нет — бесплатный аккаунт и обратно сюда
          <Redirect to={href.join(href.chat())} />
        );
      break;
    case "not_found":
      view = <NotFound />;
      break;
  }

  return (
    <HeroDataProvider value={data}>
      <PlayerProvider
        onPaywall={(ordinal, reason) => {
          // Пилот доиграл, пока человек заполнял регистрацию или смотрел аккаунт, — не
          // выдёргиваем его с экрана: пейвол ждёт в «Плеере» карточкой «That was the pilot»
          if (reason === "ended" && route.name !== "listen") return;
          navigate(href.plans({ episode: ordinal }));
        }}
      >
        {/* У переписки своя шапка и свой низ: общая полоса и мини-плеер ей мешают */}
        <Shell overlay={route.name === "hero"} showMini={route.name !== "listen" && route.name !== "chat"}>
          {route.name !== "chat" && <TopBar route={route} overlay={route.name === "hero"} />}
          {view}
        </Shell>
      </PlayerProvider>
    </HeroDataProvider>
  );
}

/** Обёртка экрана: отступ снизу под мини-плеер, чтобы он не закрывал последние строки. */
function Shell({ children, overlay, showMini }: { children: ReactNode; overlay: boolean; showMini: boolean }) {
  const player = usePlayer();
  // Дослушанная дорожка — не повод занимать низ экрана (после пилота там пейвол)
  const mini = showMini && player.track !== null && player.status !== "ended" && player.status !== "idle";
  return (
    <div className={`app${overlay ? " app--overlay" : ""}${mini ? " app--mini" : ""}`}>
      {children}
      {mini && <MiniPlayer />}
    </div>
  );
}

function Redirect({ to }: { to: string }) {
  useEffect(() => navigate(to, { replace: true }), [to]);
  return null;
}

function Splash() {
  return (
    <main className="center-screen">
      <Spinner label={t("app.loading")} size={32} />
    </main>
  );
}

function Fatal({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const missing = isApiError(error, "not_found");
  return (
    <main className="center-screen container narrow">
      <h1>{missing ? t("app.missing.title") : t("app.fatal.title")}</h1>
      <p className="muted">{missing ? t("app.missing.body") : describeError(error)}</p>
      {!missing && (
        <button type="button" className="btn btn--primary" onClick={onRetry}>
          {t("app.retry")}
        </button>
      )}
    </main>
  );
}
